import { useSyncExternalStore } from "react";

/**
 * Si hay conexión con el servidor, o no.
 *
 * `navigator.onLine` no vale: dice «sí» con la WiFi conectada a un router sin
 * internet, que es exactamente como se cae la red en un local. Lo que vale es
 * si las peticiones llegan: cada fallo de red lo apunta `api()`, cada
 * respuesta lo desmiente, y mientras esté caída se hace ping al servidor
 * cada pocos segundos para enterarse en cuanto vuelva.
 *
 * Vive fuera de React porque lo consultan `api()` y la cola de entregas, que
 * no son componentes.
 */

let enLinea = true;
let desdeCuando: number | null = null;
const oyentes = new Set<() => void>();
let vigilante: ReturnType<typeof setInterval> | null = null;

const CADA_CUANTO_SE_PRUEBA_MS = 10_000;

function avisar() {
  for (const oyente of oyentes) oyente();
}

/** Lo llama `api()` cuando una petición no llega al servidor. */
export function conexionCaida(): void {
  if (!enLinea) return;
  enLinea = false;
  desdeCuando = Date.now();
  avisar();
  vigilar();
}

/** Lo llama `api()` con cada respuesta, y el ping cuando vuelve a contestar. */
export function conexionViva(): void {
  if (enLinea) return;
  enLinea = true;
  desdeCuando = null;
  if (vigilante) {
    clearInterval(vigilante);
    vigilante = null;
  }
  avisar();
}

/**
 * Prueba si el servidor contesta. Con un tope corto: aquí no se espera media
 * minuto como en una petición normal, solo se quiere saber si ya hay red.
 */
export async function probarConexion(): Promise<boolean> {
  try {
    const señal =
      typeof AbortSignal !== "undefined" && AbortSignal.timeout
        ? AbortSignal.timeout(5_000)
        : undefined;
    const r = await fetch("/api/salud", { cache: "no-store", signal: señal });
    if (r.ok) conexionViva();
    return r.ok;
  } catch {
    return false;
  }
}

function vigilar() {
  if (vigilante) return;
  vigilante = setInterval(
    () => void probarConexion(),
    CADA_CUANTO_SE_PRUEBA_MS,
  );
}

if (typeof window !== "undefined") {
  // El navegador avisa de que ha vuelto la red: se comprueba al momento en vez
  // de esperar al siguiente ping.
  window.addEventListener("online", () => void probarConexion());
  window.addEventListener("offline", () => conexionCaida());
}

export interface EstadoConexion {
  enLinea: boolean;
  /** Desde cuándo no hay red; null si la hay. */
  desdeCuando: number | null;
}

let instantanea: EstadoConexion = { enLinea, desdeCuando };
function leer(): EstadoConexion {
  if (
    instantanea.enLinea !== enLinea ||
    instantanea.desdeCuando !== desdeCuando
  ) {
    instantanea = { enLinea, desdeCuando };
  }
  return instantanea;
}

function suscribir(oyente: () => void) {
  oyentes.add(oyente);
  return () => {
    oyentes.delete(oyente);
  };
}

export function useConexion(): EstadoConexion {
  return useSyncExternalStore(suscribir, leer, leer);
}

/** Para las pruebas: vuelve al estado de recién cargado. */
export function reiniciarConexion(): void {
  enLinea = true;
  desdeCuando = null;
  if (vigilante) {
    clearInterval(vigilante);
    vigilante = null;
  }
  avisar();
}
